import { useEffect, useState, useMemo } from "react";
import api from "@/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ROLE_LABELS } from "@/auth";
import { Search } from "lucide-react";

export default function VolunteersPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/volunteers").then((r) => setList(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return list;
    return list.filter((v) =>
      [v.full_name, v.curp, v.colonia, v.municipio, v.seccion_electoral, v.email]
        .filter(Boolean).join(" ").toLowerCase().includes(t)
    );
  }, [list, q]);

  return (
    <div className="space-y-6" data-testid="volunteers-page">
      <div>
        <span className="tracking-label text-blue-700">Padrón</span>
        <h1 className="mt-2 font-display font-black text-3xl text-slate-900">Voluntarios</h1>
        <p className="text-sm text-slate-600 mt-1">{list.length} registros visibles</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nombre, CURP, colonia, sección…"
          className="pl-10"
          data-testid="volunteers-search"
        />
      </div>

      <div className="surface-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-slate-500 text-center">Cargando…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <p>Aún no hay voluntarios registrados.</p>
            <p className="text-xs mt-2">Genera y aprueba un enlace de reclutamiento para comenzar.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <Th>Nombre</Th>
                  <Th>CURP</Th>
                  <Th>Sección</Th>
                  <Th>Colonia</Th>
                  <Th>Municipio</Th>
                  <Th>Rol asignado</Th>
                  <Th>Contacto</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => (
                  <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`volunteer-row-${v.id}`}>
                    <Td><div className="font-medium text-slate-900">{v.full_name}</div></Td>
                    <Td><code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{v.curp}</code></Td>
                    <Td>{v.seccion_electoral}</Td>
                    <Td>{v.colonia}</Td>
                    <Td>{v.municipio}</Td>
                    <Td>
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                        {ROLE_LABELS[v.target_role]}
                      </Badge>
                      {v.target_region_name && (
                        <div className="text-[10px] text-slate-500 mt-0.5">{v.target_region_name}</div>
                      )}
                    </Td>
                    <Td>
                      <div className="text-xs">{v.email}</div>
                      <div className="text-xs text-slate-500">{v.phone}</div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 text-left tracking-label text-slate-500 font-medium">{children}</th>;
}
function Td({ children }) {
  return <td className="px-4 py-3 align-top">{children}</td>;
}
